import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  PlusIcon,
  BookOpenIcon,
  AcademicCapIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  DocumentPlusIcon,
  ListBulletIcon,
  DocumentTextIcon,
  ClockIcon,
  TagIcon,
  CheckBadgeIcon,
  TableCellsIcon,
  ArrowsUpDownIcon,
  ChevronRightIcon,
  EyeIcon,
  LockClosedIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  MinusCircleIcon
} from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import {
  CSS,
} from "@dnd-kit/utilities";
import {
  getExams,
  deleteExam,
  deleteExamTest,
  updateExam,
  downloadExamBilorgFile,
  updateExamTestReorder
} from "../../services/Exams/ExamsService";
import AddTestToExam from './AddTestToExam';
import TestPreview from './TestBuilder/TestPreview';
import { Spinner, useMediaQuery } from './TestBuilder/ui';
import { getEntityLabel } from '../../utils/entityLabel';
import { getApiErrorCode, getApiErrorMessage, getCodedErrorMessage } from '../../utils/apiError';
import i18n from "../../lang/i18n";
import { HelpTitle } from '../../components/Help/HelpButton';
import ExamsGuide from '../../components/Help/guides/ExamsGuide';
import OverflowMenu from '../../components/ui/OverflowMenu';
import GripIcon from '../../components/Icons/GripIcon';
import ExamWordDialog from './word/ExamWordDialog';
import ExamDocuments from './word/ExamDocuments';
import WordPrintFields, { fieldErrorText, printCodeProblem, wordPrintPayload } from './word/WordPrintFields';
import { isLiveJob } from './word/wordText';
import {
  ExamStatusBadge,
  ExamStatusDialog,
  MOVE_ICONS,
  STATUS_MOVES,
  examStatus,
  isDeletable,
  needsConfirm,
  useChangeExamStatus,
} from './ExamStatus';

// Shared presentational class strings (match the app-wide design language)
const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';
const backLinkClass =
  `mb-2 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-lg text-sm font-medium text-slate-500 transition duration-200 hover:text-slate-900 md:mb-4 md:min-h-0 ${focusRing}`;
// The dialogs' footer buttons: 44px on phones, where they stack full width.
const buttonBaseClass =
  `inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 text-sm font-medium transition duration-200 sm:h-10 ${focusRing} disabled:cursor-not-allowed disabled:opacity-50`;
const primaryButtonClass = 'bg-blue-600 text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700';
const secondaryButtonClass =
  'bg-white text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900';
// The header's actions (FolderBar's look): the one primary, quiet ones beside it on wide screens; 44px on phones,
// where the primary shares its row with "⋯" only.
const headerPrimaryClass =
  `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-3.5 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 md:h-9 max-md:flex-1 ${focusRing}`;
const quietButtonClass =
  `inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-slate-600 transition duration-200 hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;
// An archived exam on a phone: no Add test, so Word leads the row beside "⋯".
const wordPhoneClass =
  `inline-flex h-11 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3.5 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;
// The status bar's moves: a draft's activation (the next step) framed, the other moves quiet.
const moveMainClass =
  `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3.5 text-sm font-medium text-slate-800 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 enabled:hover:bg-slate-50 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 md:h-9 max-sm:flex-1 ${focusRing}`;
const moveQuietClass =
  `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-slate-700 transition duration-200 enabled:hover:bg-white/80 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 md:h-9 ${focusRing}`;
const overlayClass =
  'fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4';
const panelClass =
  'w-full overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5 motion-safe:animate-fade-in';
const footerClass =
  'flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end';
const closeButtonClass =
  `rounded-lg p-2 text-slate-400 transition duration-200 hover:bg-slate-100 hover:text-slate-700 ${focusRing}`;
const optionButtonClass =
  `group flex w-full items-center gap-4 rounded-xl border border-slate-200 p-4 text-left transition duration-200 hover:border-blue-300 hover:bg-blue-50/50 ${focusRing}`;
const labelClass = 'mb-1.5 block text-sm font-medium text-slate-700';
const fieldClass =
  'block w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 read-only:bg-slate-50 read-only:text-slate-600';
const dialogErrorClass =
  'flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 outline-none';

// The exam's tests in their order.
const sortedTests = (examTests) => [...(examTests || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

// The edit form's fields as the exam has them (the Word's language and variant code: "" is none).
const examFormData = (exam) => ({
  name: exam?.name || '',
  description: exam?.description || '',
  duration_minutes: exam?.duration_minutes || '',
  passing_score: exam?.passing_score || '',
  language: exam?.language || '',
  print_code: exam?.print_code || ''
});

// The edit form's fields that show the server's own errors under them; the others' go in the form's message.
const WORD_FIELDS = ['language', 'print_code'];

// The exam's category by name, or "" when it has none (the API gives its uuid and `category_name`).
const examCategoryName = (exam) =>
  exam?.category_name || (exam?.category && typeof exam.category === 'object' ? getEntityLabel(exam.category, '') : '');

// A refusal takes the focus once it shows and nothing is being sent: the pressed button lost it while disabled.
const useRefusalFocus = (ref, refusal, busy) => {
  useEffect(() => {
    if (refusal && !busy) ref.current?.focus();
  }, [ref, refusal, busy]);
};

// The test's name as its row shows it (and as the reordering announces it).
const examTestName = (t, examTest, index) => examTest?.test?.name || t('exam_bank.view.test_fallback', { number: index + 1 });

// Sortable Test Item Component
// `readOnly` (an archived exam): no dragging and no removing; the test opens to be viewed (`onEdit` then shows it
// read-only). The grip moves the test, with the mouse, a finger or the keyboard (Space, the arrows, Space). The
// bin shows on the row the mouse is on, where there is a mouse; touch screens show it at once (as does the
// keyboard's focus).
const SortableTestItem = ({
  test,
  index,
  onEdit,
  onDelete,
  deleteLoading,
  readOnly = false
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: test.uuid, disabled: readOnly });
  const { t } = useTranslation();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const description = test.test?.description;
  const name = examTestName(t, test, index);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group rounded-xl border bg-white p-3 transition-colors duration-200 hover:border-slate-300 sm:p-4 ${
        isDragging ? 'border-blue-300 shadow-lg ring-4 ring-blue-600/10' : 'border-slate-200'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {/* The grip, then the number and the name. The title keeps at least 16rem: on a narrow row (a phone, a
            tablet beside the sidebar) the count and the buttons wrap under it instead of squeezing it. The grip is
            44px on phones (into the row's padding); touch-none: on a touch screen it drags instead of scrolling. */}
        <div className="flex min-w-0 flex-[1_1_16rem] items-center gap-3">
          {!readOnly && (
          <button
            ref={setActivatorNodeRef}
            type="button"
            {...attributes}
            {...listeners}
            aria-label={t('exam_bank.view.dnd.handle', { name })}
            className={`-my-1.5 -ml-2 -mr-1 inline-flex h-11 w-11 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-600 active:cursor-grabbing sm:m-0 sm:h-8 sm:w-6 ${focusRing}`}
          >
            <GripIcon className="h-5 w-5" />
          </button>
          )}

          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-xs font-semibold tabular-nums text-blue-700 ring-1 ring-inset ring-blue-600/10">
            {index + 1}
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium text-slate-900">
              {name}
            </h3>
            {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
          </div>
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          <span className="mr-1 whitespace-nowrap text-sm tabular-nums text-slate-500">
            {t('exam_bank.view.questions_count', { count: test.test?.questions_count || 0 })}
          </span>
          <button
            onClick={() => onEdit(test.test?.uuid)}
            className={`inline-flex h-11 items-center gap-1 rounded-lg px-2.5 text-sm font-medium text-blue-600 transition duration-200 hover:bg-blue-50 hover:text-blue-700 sm:h-9 ${focusRing}`}
          >
            {readOnly ? <EyeIcon className="h-4 w-4" aria-hidden="true" /> : <PencilIcon className="h-4 w-4" aria-hidden="true" />}
            {readOnly ? t('exam_bank.view.open_test') : t('buttons.edit')}
          </button>
          {!readOnly && (
          <button
            onClick={() => onDelete(test)}
            disabled={deleteLoading}
            className={`inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-400 transition duration-200 hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 sm:w-9 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 ${focusRing}`}
            title={t('exam_bank.view.remove_test')}
            aria-label={t('exam_bank.view.remove_test_named', { name: test.test?.name || t('exam_bank.untitled') })}
          >
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PANEL_TONES = {
  draft: { box: 'bg-amber-50/70', icon: InformationCircleIcon, iconClass: 'text-amber-600' },
  active: { box: 'bg-emerald-50/70', icon: CheckCircleIcon, iconClass: 'text-emerald-600' },
  archived: { box: 'bg-slate-50', icon: LockClosedIcon, iconClass: 'text-slate-500' },
};

// Under the exam's header: what its status means for students and the moves from it (a draft's activation
// first, framed; the other moves are rare, and quiet). `onMove(status)` changes it; `pending` is the status being
// sent, `error` a refusal to show (it takes the focus through `errorRef`). Activation needs a test with questions
// (400 exam_empty): without one it is offered disabled, and the hint says what is missing.
function StatusPanel({ status, hasQuestions, pending, error, onMove, onDismissError, textRef, errorRef }) {
  const { t } = useTranslation();
  const hintId = useId();
  const tone = PANEL_TONES[status];
  if (!tone) return null;
  const Icon = tone.icon;
  const moves = STATUS_MOVES[status] || [];
  const needsQuestions = status !== 'active' && !hasQuestions;
  return (
    <div className={`border-b border-slate-200 px-5 py-4 sm:px-6 ${tone.box}`}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.iconClass}`} aria-hidden="true" />
          <div ref={textRef} tabIndex={-1} aria-live="polite" className="min-w-0 rounded text-sm text-slate-700 outline-none">
            <p className="font-medium text-slate-900">{t(`exam_bank.status_actions.state.${status}`)}</p>
            {needsQuestions && (
              <p id={hintId} className="mt-0.5 text-slate-600">
                {t(status === 'archived' ? 'exam_bank.status_actions.state.needs_questions_archived' : 'exam_bank.status_actions.state.needs_questions')}
              </p>
            )}
          </div>
        </div>
        {moves.length > 0 && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {moves.map((move) => {
              const blocked = move === 'active' && needsQuestions;
              const main = status === 'draft' && move === 'active';
              const MoveIcon = MOVE_ICONS[move];
              return (
                <button
                  key={move}
                  type="button"
                  onClick={() => onMove(move)}
                  disabled={!!pending || blocked}
                  aria-describedby={blocked ? hintId : undefined}
                  className={main ? moveMainClass : moveQuietClass}
                >
                  {pending === move ? (
                    <Spinner />
                  ) : (
                    <MoveIcon className={`h-4 w-4 shrink-0 ${main ? 'text-emerald-600' : 'text-slate-500'}`} aria-hidden="true" />
                  )}
                  {t(`exam_bank.status_actions.${move}`)}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {error && (
        <div
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 outline-none"
        >
          <span className="flex min-w-0 items-start gap-2">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0">{error}</span>
          </span>
          <button
            type="button"
            onClick={onDismissError}
            aria-label={t('buttons.close')}
            className={`shrink-0 rounded-md p-0.5 text-red-400 transition duration-200 hover:bg-red-100 hover:text-red-600 ${focusRing}`}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

const ViewExams = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // The header keeps one row on phones: Add test and "⋯" (Word inside it); wide screens show Word beside them.
  const wide = useMediaQuery('(min-width: 768px)');

  const [exam, setExam] = useState(null);
  const [sortableTests, setSortableTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [bilorgLoading, setBilorgLoading] = useState(false);

  // The Word dialog (mounted while open), and a job of it that was under way when it closed: the dialog shows that
  // job again when it opens and the server no longer names one under way.
  const [wordOpen, setWordOpen] = useState(false);
  const [rememberedJob, setRememberedJob] = useState(null);

  // State for action selection modal
  const [showActionModal, setShowActionModal] = useState(false);
  const [showAddTestModal, setShowAddTestModal] = useState(false);

  // State for test deletion (a test is removed from the exam, not deleted)
  const [testToDelete, setTestToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Deleting the exam (a draft): its confirmation, and the refusal ({code, message}) said in it; a taken exam
  // (409 exam_has_attempts) is offered the archive there instead.
  const [showDeleteExam, setShowDeleteExam] = useState(false);
  const [deleteExamLoading, setDeleteExamLoading] = useState(false);
  const [deleteExamError, setDeleteExamError] = useState(null);
  const deleteExamErrorRef = useRef(null);

  // State for exam editing
  const [isEditingExam, setIsEditingExam] = useState(false);
  const [editExamData, setEditExamData] = useState(examFormData(null));
  // The Word's fields' errors (the client's check or the server's {language: [..]}, {print_code: [..]}), and the
  // field the form opens at (the Word dialog's "choose the language" opens it at the language).
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editFocus, setEditFocus] = useState(null);
  const editLanguageRef = useRef(null);
  const editCodeRef = useRef(null);

  // A refused removal or save, said in its dialog: the page behind it is inert while the dialog is open.
  const [deleteError, setDeleteError] = useState(null);
  const [editError, setEditError] = useState(null);
  const deleteErrorRef = useRef(null);
  const editErrorRef = useRef(null);
  const editNameRef = useRef(null);
  const firstOptionRef = useRef(null);

  // The test of an archived exam, shown read-only (the builder would change it).
  const [viewTestUuid, setViewTestUuid] = useState(null);

  // Bumped to read the documents list again (an export ended).
  const [documentsKey, setDocumentsKey] = useState(0);

  // The status: a direct change (activation, out of the archive), or the move awaiting its confirmation, and
  // the refusal of the last change ({code, message, id, questionsKey}: exam_empty, ...). Its text takes the focus
  // after a change, the refusal after a refusal.
  const { change: changeStatus, pending: statusPending } = useChangeExamStatus();
  const [confirmMove, setConfirmMove] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const statusTextRef = useRef(null);
  const statusErrorRef = useRef(null);
  // The reads of the exam: one overtaken by a newer read or by a status change is dropped.
  const readGenRef = useRef(0);
  // The test a moved test was last announced over: its own place is not announced as a move when it is picked up.
  const announcedOverRef = useRef(null);

  // Drag and drop sensors: the mouse or a finger on the grip, and the keyboard (as the test builder's list).
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const fetchExam = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!uuid) {
          setError(i18n.t('exam_bank.view.uuid_missing'));
          return;
        }
        console.log('Fetching exam data for UUID:', uuid);

        const data = await getExams({ params: { uuid: uuid } });
        console.log('Exam Detail API Response:', data);

        if (data) {
          setExam(data);
          // Sort tests by order and initialize sortableTests
          setSortableTests(sortedTests(data.exam_tests));

          // Initialize data for editing
          setEditExamData(examFormData(data));
        } else {
          setError(i18n.t('exam_taking.exam_not_found'));
        }
      } catch (err) {
        setError(i18n.t('exam_bank.view.load_error'));
        console.error('Error fetching exam:', err);
      } finally {
        setLoading(false);
      }
    };

    if (uuid) {
      fetchExam();
    }
  }, [uuid]);

  // Sync sortableTests with exam.exam_tests (без изменений)
  useEffect(() => {
    if (exam?.exam_tests) {
      setSortableTests(sortedTests(exam.exam_tests));
    }
  }, [exam?.exam_tests]);

  // The exam as it is now, with its tests in order (null when a newer read or a status change overtook this one).
  const readExam = useCallback(async () => {
    const gen = ++readGenRef.current;
    const updatedExam = await getExams({ params: { uuid } });
    if (gen !== readGenRef.current) return null;
    setExam(updatedExam);
    setSortableTests(sortedTests(updatedExam.exam_tests));
    return updatedExam;
  }, [uuid]);

  // Back on the tab: read again (a test or its questions may have changed in another tab), so that what the status
  // panel offers holds.
  const loaded = !!exam;
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && loaded) readExam().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [readExam, loaded]);

  const status = examStatus(exam);
  // A draft is activated only with a test that has questions (else 400 exam_empty).
  const hasQuestions = sortableTests.some((examTest) => (examTest.test?.questions_count || 0) > 0);
  // The tests and their questions as the page knows them. A refusal for "no test with questions" goes once they
  // change and the exam has one (a test added here, or questions added in another tab); until then it holds.
  const questionsKey = sortableTests.map((examTest) => `${examTest.uuid}:${examTest.test?.questions_count || 0}`).join();
  const statusRefusal =
    statusError && !(statusError.code === 'exam_empty' && hasQuestions && statusError.questionsKey !== questionsKey)
      ? statusError.message
      : '';

  useRefusalFocus(statusErrorRef, statusError?.id, statusPending);
  useRefusalFocus(deleteErrorRef, deleteError, deleteLoading);
  useRefusalFocus(editErrorRef, editError, updateLoading);
  useRefusalFocus(deleteExamErrorRef, deleteExamError, deleteExamLoading);
  // A Word field the save refused takes the focus once the form is enabled again (it is disabled while saving).
  const [editErrorField, setEditErrorField] = useState(null);
  useEffect(() => {
    if (!editErrorField || updateLoading) return;
    (editErrorField === 'language' ? editLanguageRef : editCodeRef).current?.focus();
    setEditErrorField(null);
  }, [editErrorField, updateLoading]);
  // The settings opened from the Word dialog give the focus back to the Word action when they close: the dialog's
  // link that opened them is gone (phones: "⋯", which holds Word).
  const settingsFromWordRef = useRef(false);
  useEffect(() => {
    if (isEditingExam || !settingsFromWordRef.current) return;
    settingsFromWordRef.current = false;
    requestAnimationFrame(() => document.querySelector("[data-testid='exam-word'], [data-testid='exam-actions']")?.focus());
  }, [isEditingExam]);

  const handleDownloadBilorg = async () => {
    setBilorgLoading(true);
    setError(null);
    try {
      await downloadExamBilorgFile({ uuid });
    } catch (err) {
      // A refusal with a code says why: 403 answers_hidden (the exam has questions whose answers are not the user's)
      // calmly, as the Word's key held back; anything else, the export's own message.
      const text = getCodedErrorMessage(t, err, t('exam_bank.view.bilorg_error'));
      setError(getApiErrorCode(err) === 'answers_hidden' ? { text, held: true } : text);
    } finally {
      setBilorgLoading(false);
    }
  };

  // The Word: its dialog says what will be printed and makes it.
  const openWord = () => setWordOpen(true);
  const closeWord = useCallback(() => {
    setWordOpen(false);
    // Files made while it was open (or a job that ended meanwhile) show in the list.
    setDocumentsKey((prev) => prev + 1);
  }, []);
  // A job under way is remembered for the next opening; one that ended is forgotten (null: the dialog had none yet).
  const handleWordJob = useCallback((job) => {
    if (job) setRememberedJob(isLiveJob(job) ? job.uuid : null);
  }, []);
  const handleWordFiles = useCallback(() => setDocumentsKey((prev) => prev + 1), []);

  // Остальные функции без изменений...
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      try {
        const oldIndex = sortableTests.findIndex((item) => item.uuid === active.id);
        const newIndex = sortableTests.findIndex((item) => item.uuid === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        const newOrder = newIndex + 1;
        const movedItem = sortableTests[oldIndex];

        const newArr = arrayMove(sortableTests, oldIndex, newIndex);
        setSortableTests(newArr);

        await updateExamTestReorder({
          params: { uuid: movedItem.uuid },
          data: { order: newOrder }
        });

        await readExam();

      } catch (err) {
        console.error('Error reordering test:', err);
        setError(getCodedErrorMessage(t, err, t('exam_bank.view.reorder_error')));
        // The order as it is on the server (and the status: a 409 exam_archived turns the page read-only).
        await reloadExam().catch(() => {});
      }
    }
  };

  // Read again after the exam changed under the page (archived meanwhile, ...): the refusal of an earlier status
  // change described it as it was.
  const reloadExam = async () => {
    await readExam();
    setStatusError(null);
  };

  // A refused write: its message; a 409 exam_archived (archived meanwhile, e.g. in another tab) also reads the
  // exam again, so that the page turns read-only, and the status text, which says so, takes the focus (the button
  // that was used is gone).
  const showWriteError = (err, fallbackKey) => {
    setError(getCodedErrorMessage(t, err, t(fallbackKey)));
    if (getApiErrorCode(err) === 'exam_archived') {
      reloadExam()
        .then(() => statusTextRef.current?.focus())
        .catch(() => {});
    }
  };

  // The exam's new status; its meaning (the panel's text) takes the focus, the panel's buttons having changed.
  const applyStatus = (changed) => {
    // A read sent before the change would bring the old status back.
    readGenRef.current += 1;
    setExam((prev) => ({ ...prev, status: changed.status }));
    setStatusError(null);
    requestAnimationFrame(() => statusTextRef.current?.focus());
  };

  // Activation and the way out of the archive go at once; taking the exam from students is confirmed first.
  const handleMove = async (move) => {
    setStatusError(null);
    if (needsConfirm(examStatus(exam), move)) {
      setConfirmMove(move);
      setConfirmOpen(true);
      return;
    }
    const result = await changeStatus(exam, move);
    if (!result.error) {
      applyStatus(result.exam);
      return;
    }
    setStatusError({ code: result.code, message: result.error, id: Date.now(), questionsKey });
    // No test with questions though the page saw one: its tests are read again, and the panel says what is missing.
    if (result.code === 'exam_empty') readExam().catch(() => {});
  };

  const handleBackToExams = () => {
    navigate('/exams_list');
  };

  const handleAddTestClick = () => {
    setShowActionModal(true);
  };

  const handleCreateNewTest = () => {
    setShowActionModal(false);
    navigate(`/exams_list/${uuid}/create_test`);
  };

  const handleAddExistingTest = () => {
    setShowActionModal(false);
    setShowAddTestModal(true);
  };

  // The test builder's Back and Done return here.
  const handleEditTest = (testUuid) => {
    navigate(`/test/${testUuid}`, { state: { from: `/exams_list/${uuid}` } });
  };

  // Tests were added: the dialog closes and the exam is read again.
  const handleTestAdded = async () => {
    setShowAddTestModal(false);
    try {
      await readExam();
    } catch (err) {
      console.error('Error refreshing exam data:', err);
      setError(t('exam_bank.view.refresh_error'));
    }
  };

  const handleDeleteTest = (test) => {
    setTestToDelete(test);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  // Removes the test from the exam (DELETE of its exam-test row); the test itself stays in the test bank.
  const confirmDeleteTest = async () => {
    if (!testToDelete) return;

    try {
      setDeleteLoading(true);
      setDeleteError(null);
      console.log('Removing test from exam:', testToDelete.uuid);

      await deleteExamTest({ params: { uuid: testToDelete.uuid } });
    } catch (err) {
      console.error('Error removing test:', err);
      if (getApiErrorCode(err) === 'exam_archived') {
        // Archived meanwhile: nothing is removed from it, so the dialog closes and the page, read-only now, says why.
        cancelDeleteTest();
        showWriteError(err, 'exam_bank.view.remove_test_error');
      } else {
        setDeleteError(getCodedErrorMessage(t, err, t('exam_bank.view.remove_test_error')));
      }
      return;
    } finally {
      setDeleteLoading(false);
    }

    cancelDeleteTest();
    try {
      await readExam();
    } catch (err) {
      console.error('Error refreshing exam data:', err);
      setError(t('exam_bank.view.refresh_error'));
    }
  };

  const cancelDeleteTest = () => {
    setShowDeleteModal(false);
    setTestToDelete(null);
    setDeleteError(null);
  };

  const openDeleteExam = () => {
    setDeleteExamError(null);
    setShowDeleteExam(true);
  };

  const closeDeleteExam = () => {
    setShowDeleteExam(false);
    setDeleteExamError(null);
  };

  // Deletes the exam (DELETE exams/<uuid>/) and goes back to the list. A taken exam stays (409
  // exam_has_attempts): the dialog says so and offers the archive; one archived meanwhile (409 exam_archived,
  // which comes first) closes the dialog, and the page, read-only now, says why.
  const confirmDeleteExam = async () => {
    setDeleteExamLoading(true);
    setDeleteExamError(null);
    try {
      await deleteExam({ params: { uuid } });
    } catch (err) {
      console.error('Error deleting exam:', err);
      const code = getApiErrorCode(err);
      setDeleteExamLoading(false);
      if (code === 'exam_archived') {
        closeDeleteExam();
        showWriteError(err, 'exam_bank.delete_exam.error');
      } else {
        setDeleteExamError({ code, message: getCodedErrorMessage(t, err, t('exam_bank.delete_exam.error')) });
      }
      return;
    }
    toast.success(t('exam_bank.delete_exam.deleted'));
    navigate('/exams_list', { replace: true });
  };

  // The archive instead of the delete the server refused: the status bar's own move, with its confirmation.
  const archiveInstead = () => {
    closeDeleteExam();
    handleMove('archived');
  };

  // `focus`: the field the form opens at ("language" from the Word dialog), else the name.
  const handleEditExam = (focus = null) => {
    setEditFocus(focus);
    setIsEditingExam(true);
    setEditError(null);
    setEditFieldErrors({});
    setEditExamData(examFormData(exam));
  };

  // The Word dialog's links to the settings: it closes, and the form opens (at the language when that was asked).
  const openSettingsFromWord = (field) => {
    settingsFromWordRef.current = true;
    closeWord();
    handleEditExam(field === 'language' ? 'language' : null);
  };

  const handleCancelEditExam = () => {
    setIsEditingExam(false);
    setEditError(null);
    setEditFieldErrors({});
    setEditExamData(examFormData(exam));
  };

  // The form differs from the exam: closing it without saving is confirmed.
  const editDirty =
    isEditingExam &&
    Object.entries(examFormData(exam)).some(([field, value]) => String(editExamData[field] ?? '') !== String(value));

  // Escape, a click outside, the X, Cancel and Close: nothing closes while saving.
  const closeEditExam = () => {
    if (updateLoading) return;
    if (editDirty && !window.confirm(t('exam_bank.edit_exam.discard_confirm'))) return;
    handleCancelEditExam();
  };

  const handleSaveExam = async () => {
    // The variant code with a control or invisible character is refused here already (the server would too).
    const codeProblem = printCodeProblem(t, editExamData.print_code);
    if (codeProblem) {
      setEditFieldErrors({ print_code: codeProblem });
      setEditErrorField('print_code');
      return;
    }
    setUpdateLoading(true);
    setEditError(null);
    setEditFieldErrors({});
    try {
      const data = {
        name: editExamData.name.trim(),
        description: editExamData.description.trim(),
        duration_minutes: parseInt(editExamData.duration_minutes) || 0,
        passing_score: parseInt(editExamData.passing_score) || 0,
        ...wordPrintPayload(editExamData)
      };

      console.log('Updating exam:', { uuid, data });
      await updateExam({ params: { uuid }, data });
    } catch (err) {
      console.error('Error updating exam:', err);

      const errors = err?.response?.data;
      // Only a { field: messages } body is listed. A { detail, code } error (no permission, not found), an HTML
      // error page or no answer get the translated code or the generic message. (Exams have a `code` field,
      // so it is `detail` that tells the two apart.) Said in the dialog: the page behind it is inert. The Word's
      // fields show theirs under them (the first one takes the focus); the others are listed in the message.
      if (errors && typeof errors === 'object' && !Array.isArray(errors) && typeof errors.detail !== 'string') {
        const fieldErrors = {};
        WORD_FIELDS.forEach((field) => {
          const text = fieldErrorText(errors[field]);
          if (text) fieldErrors[field] = text;
        });
        setEditFieldErrors(fieldErrors);
        const errorMessages = Object.entries(errors)
          .filter(([field]) => !fieldErrors[field])
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        setEditError(errorMessages ? t('exam_bank.view.validation_error', { details: errorMessages }) : null);
        setUpdateLoading(false);
        if (fieldErrors.language || fieldErrors.print_code) setEditErrorField(fieldErrors.language ? 'language' : 'print_code');
        return;
      } else {
        setEditError(getApiErrorMessage(t, err, 'exam_bank.view.update_error'));
        // Archived meanwhile: the page and the form turn read-only (the form stays open, its text can be copied).
        if (getApiErrorCode(err) === 'exam_archived') reloadExam().catch(() => {});
      }
      setUpdateLoading(false);
      return;
    }

    console.log('Exam updated successfully');
    // The dialog closes on the exam as saved.
    try {
      await readExam();
    } catch (err) {
      console.error('Error refreshing exam data:', err);
      setError(t('exam_bank.view.refresh_error'));
    }
    setUpdateLoading(false);
    setIsEditingExam(false);
  };

  const handleInputChange = (field, value) => {
    setEditExamData(prev => ({
      ...prev,
      [field]: value
    }));
    if (editFieldErrors[field]) setEditFieldErrors((prev) => ({ ...prev, [field]: null }));
  };

  const clearError = () => {
    setError(null);
  };
  // The page's message: its text, or {text, held} for a file held back from the user (said calmly, not as a failure).
  const errorText = typeof error === 'string' ? error : error?.text;
  const errorHeld = error?.held === true;

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex h-64 items-center justify-center gap-3 text-sm text-slate-500">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
          <span>{t('exam_taking.loading_exam')}</span>
        </div>
      </div>
    );
  }

  if (error && !exam) {
    return (
      <div className="w-full motion-safe:animate-fade-in">
        <div className="w-full">
          <button
            onClick={handleBackToExams}
            className={backLinkClass}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t('exam_taking.back_to_exams')}
          </button>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col items-center py-8 text-center">
              <div className="mb-3 rounded-full bg-red-50 p-3 text-red-600" aria-hidden="true">
                <ExclamationTriangleIcon className="h-6 w-6" />
              </div>
              <div className="mb-1 text-sm font-semibold text-slate-900">{t('exam_bank.view.error_title')}</div>
              <div className="text-sm text-slate-500">{errorText}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="w-full">
        <div className="flex h-64 items-center justify-center">
          <div className="text-sm text-slate-500">{t('exam_taking.exam_not_found')}</div>
        </div>
      </div>
    );
  }

  const hasTests = sortableTests && sortableTests.length > 0;
  // An archived exam is read-only (the server answers 409 exam_archived): no editing, adding, removing or reordering.
  const readOnly = status === 'archived';
  const categoryName = examCategoryName(exam);
  // A refused delete of an exam that was taken: deleting again cannot work, the archive can.
  const deleteRefusedTaken = deleteExamError?.code === 'exam_has_attempts';
  const ArchiveIcon = MOVE_ICONS.archived;

  // The rare actions, in "⋯" (phones: Word too, but on an archived exam, where Word leads the row). Deleting is a
  // draft's only; it is confirmed in its dialog. An archived exam on a wide screen would keep Excel for bilorg
  // alone in "⋯": it is a quiet button beside Word there instead.
  const wordInMenu = !wide && !readOnly;
  const bilorgButton = wide && readOnly;
  const menuItems = [
    wordInMenu && {
      key: 'word',
      label: t('exam_bank.view.word_generate'),
      Icon: DocumentTextIcon,
      onClick: openWord,
    },
    !bilorgButton && {
      key: 'bilorg',
      label: t('exam_bank.view.bilorg_export'),
      Icon: TableCellsIcon,
      onClick: handleDownloadBilorg,
      disabled: bilorgLoading,
    },
    !readOnly && { key: 'edit', label: t('exam_bank.view.edit_exam'), Icon: PencilIcon, onClick: () => handleEditExam() },
    isDeletable(exam) && {
      key: 'delete',
      label: t('exam_bank.list.delete_exam'),
      Icon: TrashIcon,
      danger: true,
      onClick: openDeleteExam,
    },
  ];
  // The bilorg export run from "⋯" says here that it is running: the menu closed on the click. (Word has its dialog.)
  const exportBusy = bilorgLoading && !bilorgButton ? t('exam_bank.view.bilorg_exporting') : '';

  // What a screen reader hears while a test is moved (by the keyboard or the mouse): the test by its name and its
  // place in the exam, as the test builder says it for its questions.
  const testAt = (id) => sortableTests.findIndex((examTest) => examTest.uuid === id);
  const announce = (event, id, values) => {
    const at = testAt(id);
    return t(`exam_bank.view.dnd.${event}`, { name: examTestName(t, sortableTests[at], at), ...values });
  };
  const dndAccessibility = {
    screenReaderInstructions: { draggable: t('exam_bank.view.dnd.instructions') },
    announcements: {
      onDragStart: ({ active }) => {
        announcedOverRef.current = active.id;
        return announce('picked', active.id, { position: testAt(active.id) + 1, total: sortableTests.length });
      },
      // Picked up, the test is over its own place at once: that would drown "picked up". Only a new place is said.
      onDragOver: ({ active, over }) => {
        if (!over || over.id === announcedOverRef.current) return undefined;
        announcedOverRef.current = over.id;
        return announce('moved', active.id, { position: testAt(over.id) + 1 });
      },
      onDragEnd: ({ active, over }) => (over ? announce('dropped', active.id, { position: testAt(over.id) + 1 }) : undefined),
      onDragCancel: ({ active }) => announce('cancelled', active.id),
    },
  };

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Back button */}
        <button
          onClick={handleBackToExams}
          className={backLinkClass}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('exam_taking.back_to_exams')}
        </button>

        {/* Note: the fade-in sits on the card, not the page root, so the modals below are not trapped under the navbar */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm motion-safe:animate-fade-in">
          {/* Header: the name with the module's guide ("?"), its description and status; one primary action (Add
              test), Word quiet beside it on wide screens and the rare actions in "⋯". Phones: Add test and "⋯".
              The name keeps at least 22rem: where the actions do not fit beside it (a tablet, a half-screen window,
              a long label), they wrap under it, at the right. */}
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4 border-b border-slate-200 p-5 sm:p-6">
            <div className="flex min-w-0 flex-[1_1_22rem] gap-4">
              {/* Icon */}
              <div className="hidden h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 sm:flex" aria-hidden="true">
                <AcademicCapIcon className="h-6 w-6" />
              </div>

              {/* Exam Info */}
              <div className="min-w-0 flex-1">
                <HelpTitle as="h1" helpId="exams" helpTitle={t('help.exams.title')} guide={<ExamsGuide readOnly={readOnly} />} className="break-words">
                  {exam.name}
                </HelpTitle>
                {exam.description && <p className="mt-1 max-w-3xl text-sm text-slate-500">{exam.description}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <ExamStatusBadge status={status} />
                  {categoryName && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <TagIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                      {t('exam_bank.view.category', { category: categoryName })}
                    </span>
                  )}
                  {/* A duration of 0 says nothing (no "0" either); a passing score of 0% is a setting, shown. */}
                  {exam.duration_minutes > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <ClockIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                      {t('exam_bank.view.duration', { minutes: exam.duration_minutes })}
                    </span>
                  )}
                  {exam.passing_score != null && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckBadgeIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                      {t('exam_bank.view.passing_score', { score: exam.passing_score })}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2 max-md:w-full">
              <span
                role="status"
                className={exportBusy ? 'inline-flex items-center gap-2 text-sm text-slate-500 max-md:order-last max-md:w-full' : 'sr-only'}
              >
                {exportBusy && (
                  <>
                    <Spinner />
                    {exportBusy}
                  </>
                )}
              </span>
              {!wordInMenu && (
                <button
                  type="button"
                  onClick={openWord}
                  className={wide ? quietButtonClass : wordPhoneClass}
                  title={t('exam_bank.view.word_hint')}
                  aria-haspopup="dialog"
                  data-testid="exam-word"
                >
                  <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />
                  {t('exam_bank.view.word_generate')}
                </button>
              )}
              {bilorgButton && (
                <button
                  type="button"
                  onClick={handleDownloadBilorg}
                  disabled={bilorgLoading}
                  className={quietButtonClass}
                  title={t('exam_bank.view.bilorg_hint')}
                >
                  {bilorgLoading ? <Spinner /> : <TableCellsIcon className="h-4 w-4" aria-hidden="true" />}
                  {bilorgLoading ? t('exam_bank.view.bilorg_exporting') : t('exam_bank.view.bilorg_export')}
                </button>
              )}

              {/* Add Test Button */}
              {!readOnly && (
              <button
                type="button"
                onClick={handleAddTestClick}
                className={headerPrimaryClass}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                {t('exam_bank.view.add_test')}
              </button>
              )}

              <OverflowMenu items={menuItems} label={t('exam_bank.view.more_actions')} testId="exam-actions" />
            </div>
          </div>

          <StatusPanel
            status={status}
            hasQuestions={hasQuestions}
            pending={statusPending}
            error={statusRefusal}
            onMove={handleMove}
            onDismissError={() => setStatusError(null)}
            textRef={statusTextRef}
            errorRef={statusErrorRef}
          />

          {/* Tests Section */}
          <div className="p-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-4">
              <h2 className="text-base font-semibold text-slate-900">
                {t('exam_bank.view.tests_title')}
              </h2>
              {hasTests && !readOnly && (
                <p className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  <ArrowsUpDownIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  {t('exam_bank.view.drag_hint')}
                </p>
              )}
            </div>

            {!hasTests ? (
              /* Empty State: says where tests come from; the header's Add test is the one button for it. */
              <div className="rounded-xl border border-slate-200 bg-slate-50/60">
                <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                  <div className="mb-3 rounded-full bg-white p-3 text-slate-400 ring-1 ring-inset ring-slate-200" aria-hidden="true">
                    <BookOpenIcon className="h-6 w-6" />
                  </div>

                  <h3 className="text-sm font-medium text-slate-900">
                    {t('exam_bank.view.empty_title')}
                  </h3>

                  {!readOnly && (
                  <p className="mt-1 max-w-md text-sm text-slate-500">
                    {t('exam_bank.view.empty_hint_add', { button: t('exam_bank.view.add_test') })}
                  </p>
                  )}
                </div>
              </div>
            ) : (
              /* Tests List with Drag and Drop */
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                accessibility={dndAccessibility}
              >
                <SortableContext
                  items={sortableTests.map((test) => test.uuid)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {sortableTests.map((test, index) => (
                      <SortableTestItem
                        key={test.uuid || index}
                        test={test}
                        index={index}
                        onEdit={readOnly ? setViewTestUuid : handleEditTest}
                        onDelete={handleDeleteTest}
                        deleteLoading={deleteLoading}
                        readOnly={readOnly}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* The Word documents: a BTS export's key and student copy as one entry. */}
            <ExamDocuments examUuid={uuid} refreshKey={documentsKey} />
          </div>
        </div>

        {/* The modals are Headless UI dialogs: the focus moves in and stays there, Escape and a click outside close
            them (not while their request runs), and the page behind is inert. */}
        {/* Action selection modal */}
        {showActionModal && (
          <Dialog open onClose={() => setShowActionModal(false)} initialFocus={firstOptionRef} className="relative z-50">
            <div className={overlayClass}>
            <Dialog.Panel className={`max-w-md ${panelClass}`}>
              <div className="flex items-center justify-between px-6 pb-2 pt-6">
                <Dialog.Title as="h2" className="text-lg font-semibold text-slate-900">{t('exam_bank.add_test.title')}</Dialog.Title>
                <button
                  type="button"
                  onClick={() => setShowActionModal(false)}
                  aria-label={t('buttons.close')}
                  className={closeButtonClass}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-3 px-6 pb-6">
                <p className="mb-4 text-sm text-slate-500">
                  {t('exam_bank.add_test.choose')}
                </p>

                {/* The two ways are equal: one neutral tone for both. */}
                <button
                  ref={firstOptionRef}
                  type="button"
                  onClick={handleCreateNewTest}
                  className={optionButtonClass}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600" aria-hidden="true">
                    <DocumentPlusIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-slate-900">{t('exam_bank.add_test.create_new')}</h3>
                    <p className="text-sm text-slate-500">{t('exam_bank.add_test.create_new_hint')}</p>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-300 transition-colors duration-200 group-hover:text-blue-600" aria-hidden="true" />
                </button>

                <button
                  type="button"
                  onClick={handleAddExistingTest}
                  className={optionButtonClass}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600" aria-hidden="true">
                    <ListBulletIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-slate-900">{t('exam_bank.add_test.select_existing')}</h3>
                    <p className="text-sm text-slate-500">{t('exam_bank.add_test.select_existing_hint')}</p>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-300 transition-colors duration-200 group-hover:text-blue-600" aria-hidden="true" />
                </button>
              </div>
            </Dialog.Panel>
            </div>
          </Dialog>
        )}

        {/* Add existing test modal */}
        {showAddTestModal && (
          <AddTestToExam
            examUuid={uuid}
            onClose={() => setShowAddTestModal(false)}
            onTestAdded={handleTestAdded}
            onExamChanged={(err) => {
              // Archived meanwhile: the dialog closes and the page, read-only now, says why.
              setShowAddTestModal(false);
              showWriteError(err, 'exam_bank.select_tests.add_error');
            }}
            existingTests={sortableTests || []}
          />
        )}

        {/* Remove test modal: the test leaves this exam and stays in the test bank. */}
        {showDeleteModal && testToDelete && (
          <Dialog open onClose={() => !deleteLoading && cancelDeleteTest()} className="relative z-50">
            <div className={overlayClass}>
            <Dialog.Panel className={`max-w-md ${panelClass}`}>
              <div className="flex items-start gap-4 p-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100">
                  <MinusCircleIcon className="h-5 w-5 text-slate-600" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <Dialog.Title as="h2" className="text-lg font-semibold text-slate-900">{t('exam_bank.remove_test.title')}</Dialog.Title>
                  <p className="mt-2 text-sm text-slate-600">
                    <Trans
                      i18nKey="exam_bank.remove_test.confirm"
                      values={{ name: testToDelete.test?.name || t('exam_bank.untitled') }}
                      components={{ strong: <strong className="font-semibold text-slate-900" /> }}
                    />
                  </p>
                  <p className="mt-2 text-sm text-slate-500">
                    {t('exam_bank.remove_test.hint')}
                  </p>
                  {deleteError && (
                    <div ref={deleteErrorRef} tabIndex={-1} role="alert" className={`mt-3 ${dialogErrorClass}`}>
                      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="min-w-0">{deleteError}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className={footerClass}>
                <button
                  type="button"
                  onClick={cancelDeleteTest}
                  disabled={deleteLoading}
                  className={`${buttonBaseClass} ${secondaryButtonClass}`}
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteTest}
                  disabled={deleteLoading}
                  className={`${buttonBaseClass} bg-red-600 text-white shadow-sm shadow-red-600/20 hover:bg-red-700`}
                >
                  {deleteLoading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></div>
                  )}
                  {t('buttons.remove')}
                </button>
              </div>
            </Dialog.Panel>
            </div>
          </Dialog>
        )}

        {/* Delete the exam (a draft): confirmed here. A taken exam stays (409 exam_has_attempts): the dialog then says
            it cannot be deleted (its title too), the message offers the archive instead, and the delete button goes
            (it cannot work). */}
        {showDeleteExam && (
          <Dialog open onClose={() => !deleteExamLoading && closeDeleteExam()} className="relative z-50">
            <div className={overlayClass}>
            <Dialog.Panel className={`max-w-md ${panelClass}`} data-testid="delete-exam-dialog">
              <div className="flex items-start gap-4 p-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
                  <TrashIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <Dialog.Title as="h2" className="text-lg font-semibold text-slate-900">
                    {t(deleteRefusedTaken ? 'exam_bank.delete_exam.taken_title' : 'exam_bank.delete_exam.title')}
                  </Dialog.Title>
                  {/* What goes, and what stays: the dialog's description. The refusal of a taken exam replaces both
                      (nothing is deleted; it says why, and what can be done). */}
                  {!deleteRefusedTaken && (
                  <>
                  <Dialog.Description className="mt-2 break-words text-sm text-slate-600">
                    <Trans
                      i18nKey="exam_bank.delete_exam.confirm"
                      values={{ name: exam.name?.trim() || t('exam_bank.untitled') }}
                      components={{ strong: <strong className="font-semibold text-slate-900" /> }}
                    />
                  </Dialog.Description>
                  <Dialog.Description className="mt-2 text-sm text-slate-500">
                    {t('exam_bank.delete_exam.hint')}
                  </Dialog.Description>
                  </>
                  )}
                  {deleteExamError && (
                    <div
                      ref={deleteExamErrorRef}
                      tabIndex={-1}
                      role="alert"
                      className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 outline-none"
                    >
                      <p className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        <span className="min-w-0">{deleteExamError.message}</span>
                      </p>
                      {deleteRefusedTaken && (
                        <button
                          type="button"
                          onClick={archiveInstead}
                          className={`ml-6 mt-2 inline-flex h-11 items-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3 text-sm font-medium text-slate-800 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 sm:h-9 ${focusRing}`}
                        >
                          <ArchiveIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
                          {t('exam_bank.status_actions.archived')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className={footerClass}>
                <button
                  type="button"
                  onClick={closeDeleteExam}
                  disabled={deleteExamLoading}
                  className={`${buttonBaseClass} ${secondaryButtonClass}`}
                >
                  {deleteRefusedTaken ? t('buttons.close') : t('buttons.cancel')}
                </button>
                {!deleteRefusedTaken && (
                <button
                  type="button"
                  onClick={confirmDeleteExam}
                  disabled={deleteExamLoading}
                  className={`${buttonBaseClass} bg-red-600 text-white shadow-sm shadow-red-600/20 hover:bg-red-700`}
                >
                  {deleteExamLoading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></div>
                  )}
                  {t('buttons.delete')}
                </button>
                )}
              </div>
            </Dialog.Panel>
            </div>
          </Dialog>
        )}

        {/* Exam editing modal. An exam archived meanwhile turns it read-only: its text can still be copied. */}
        {isEditingExam && (
          <Dialog
            open
            onClose={closeEditExam}
            initialFocus={editFocus === 'language' && !readOnly ? editLanguageRef : editNameRef}
            className="relative z-50"
          >
            <div className={overlayClass}>
            <Dialog.Panel className={`flex max-h-[calc(100dvh-2rem)] max-w-lg flex-col ${panelClass}`}>
              <div className="flex items-center justify-between px-6 pb-2 pt-6">
                <Dialog.Title as="h2" className="text-lg font-semibold text-slate-900">{t('exam_bank.edit_exam.title')}</Dialog.Title>
                <button
                  type="button"
                  onClick={closeEditExam}
                  disabled={updateLoading}
                  aria-label={t('buttons.close')}
                  className={`${closeButtonClass} disabled:opacity-50`}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6 pt-2">
                <div>
                  <label htmlFor="edit-exam-name" className={labelClass}>
                    {t('exam_bank.form.name')} *
                  </label>
                  <input
                    ref={editNameRef}
                    id="edit-exam-name"
                    type="text"
                    value={editExamData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={updateLoading}
                    readOnly={readOnly}
                    className={`h-10 max-sm:h-11 ${fieldClass}`}
                    placeholder={t('exam_bank.form.name_placeholder')}
                  />
                </div>

                <div>
                  <label htmlFor="edit-exam-description" className={labelClass}>
                    {t('exam_bank.form.description')}
                  </label>
                  <textarea
                    id="edit-exam-description"
                    value={editExamData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    disabled={updateLoading}
                    readOnly={readOnly}
                    rows={3}
                    className={`resize-none py-2 ${fieldClass}`}
                    placeholder={t('exam_bank.form.description_placeholder')}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="edit-exam-duration" className={labelClass}>
                      {t('exam_bank.form.duration')}
                    </label>
                    <input
                      id="edit-exam-duration"
                      type="number"
                      value={editExamData.duration_minutes}
                      onChange={(e) => handleInputChange('duration_minutes', e.target.value)}
                      disabled={updateLoading}
                      readOnly={readOnly}
                      className={`h-10 max-sm:h-11 ${fieldClass}`}
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  <div>
                    <label htmlFor="edit-exam-passing-score" className={labelClass}>
                      {t('exam_bank.form.passing_score')}
                    </label>
                    <input
                      id="edit-exam-passing-score"
                      type="number"
                      value={editExamData.passing_score}
                      onChange={(e) => handleInputChange('passing_score', e.target.value)}
                      disabled={updateLoading}
                      readOnly={readOnly}
                      className={`h-10 max-sm:h-11 ${fieldClass}`}
                      placeholder="0"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <WordPrintFields
                  idPrefix="edit-exam"
                  language={editExamData.language}
                  printCode={editExamData.print_code}
                  onChange={handleInputChange}
                  errors={editFieldErrors}
                  disabled={updateLoading}
                  readOnly={readOnly}
                  languageRef={editLanguageRef}
                  codeRef={editCodeRef}
                  className="border-t border-slate-200 pt-4"
                />

                {editError && (
                  <div ref={editErrorRef} tabIndex={-1} role="alert" className={dialogErrorClass}>
                    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="min-w-0">{editError}</span>
                  </div>
                )}
              </div>

              <div className={footerClass}>
                <button
                  type="button"
                  onClick={closeEditExam}
                  disabled={updateLoading}
                  className={`${buttonBaseClass} ${secondaryButtonClass}`}
                >
                  {readOnly ? t('buttons.close') : t('buttons.cancel')}
                </button>
                {!readOnly && (
                <button
                  type="button"
                  onClick={handleSaveExam}
                  disabled={updateLoading || !editExamData.name.trim()}
                  className={`${buttonBaseClass} ${primaryButtonClass}`}
                >
                  {updateLoading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></div>
                  )}
                  {t('buttons.save')}
                </button>
                )}
              </div>
            </Dialog.Panel>
            </div>
          </Dialog>
        )}

        {/* The Word: what it prints, making it and its two files. Mounted while open: closing stops following its job. */}
        {wordOpen && (
          <ExamWordDialog
            examUuid={uuid}
            examName={exam.name}
            rememberedJob={rememberedJob}
            onClose={closeWord}
            onJobChange={handleWordJob}
            onFilesChanged={handleWordFiles}
            onOpenSettings={readOnly ? null : openSettingsFromWord}
          />
        )}

        {/* An archived exam's test, read-only: the builder would change it, and the archive keeps the exam as it is. */}
        <TestPreview open={!!viewTestUuid} testUuid={viewTestUuid} onClose={() => setViewTestUuid(null)} />

        {/* Taking the exam from students (archive, back to draft) is confirmed here. */}
        <ExamStatusDialog
          isOpen={confirmOpen}
          exam={exam}
          target={confirmMove}
          onClose={() => setConfirmOpen(false)}
          onChanged={applyStatus}
        />

        {/* Error message */}
        {error && (
          <div
            role="alert"
            className={`fixed bottom-4 left-4 right-4 z-50 rounded-xl border px-4 py-3 shadow-lg sm:left-auto sm:max-w-md ${
              errorHeld ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-200 bg-red-50 text-red-700'
            }`}
            data-testid={errorHeld ? 'exam-page-held' : 'exam-page-error'}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2">
                {errorHeld ? (
                  <LockClosedIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                ) : (
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
                )}
                <span className="text-sm">{errorText}</span>
              </div>
              <button
                onClick={clearError}
                aria-label={t('buttons.close')}
                className={`shrink-0 rounded-md p-1 transition duration-200 ${
                  errorHeld ? 'text-amber-700 hover:bg-amber-100 hover:text-amber-900' : 'text-red-400 hover:bg-red-100 hover:text-red-600'
                } ${focusRing}`}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewExams;
